import { useCallback, useState } from "react";
import { downloadPropertyMasterData } from "../services/propertyApiService";

// Shared report-download logic, used by both the legacy and redesigned
// Property Case Details pages — same reasoning as usePropertyAiSummary.js:
// only the rendering differs between the two.
//
// Mirrors CaseDetails.js's own fetchDocumentsArchive/cacheDocumentsArchive
// (the Cache API for the blob, a localStorage meta key for a 30-minute TTL),
// scoped to its own cache name/key prefix so neither design's cache can
// collide with Motor's "documents-tab-cache-v1". Unlike Motor's version,
// this does not extract a PDF out of the ZIP for an inline preview — nothing
// here asked for a Documents-tab-style preview, only a working download.

const CACHE_NAME = "property-report-cache-v1";
const CACHE_TTL_MS = 30 * 60 * 1000;

const getCacheKey = (refNum) => `/property-report/${refNum}`;
const getMetaKey = (refNum) => `property-report-cache-meta:${refNum}`;

const getCachedReport = async (refNum) => {
  if (!("caches" in window)) return null;

  const metaValue = window.localStorage.getItem(getMetaKey(refNum));
  if (!metaValue) return null;

  try {
    const meta = JSON.parse(metaValue);
    if (!meta.cachedAt || Date.now() - meta.cachedAt > CACHE_TTL_MS) {
      window.localStorage.removeItem(getMetaKey(refNum));
      const cache = await window.caches.open(CACHE_NAME);
      await cache.delete(getCacheKey(refNum));
      return null;
    }

    const cache = await window.caches.open(CACHE_NAME);
    const response = await cache.match(getCacheKey(refNum));
    if (!response) return null;

    return { blob: await response.blob(), fileName: meta.fileName };
  } catch (error) {
    console.error("Error reading property report cache:", error);
    return null;
  }
};

const cacheReport = async (refNum, blob, fileName) => {
  if (!("caches" in window)) return;

  const cache = await window.caches.open(CACHE_NAME);
  await cache.put(
    getCacheKey(refNum),
    new Response(blob, {
      headers: { "Content-Type": blob.type || "application/zip" },
    }),
  );

  window.localStorage.setItem(
    getMetaKey(refNum),
    JSON.stringify({ cachedAt: Date.now(), fileName }),
  );
};

const clickDownloadLink = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export function usePropertyDownloadReport() {
  const [status, setStatus] = useState("idle"); // idle | downloading | error
  const [error, setError] = useState(null);

  const download = useCallback(async (refNum) => {
    if (!refNum) return;

    setStatus("downloading");
    setError(null);

    try {
      const cached = await getCachedReport(refNum);
      if (cached) {
        clickDownloadLink(cached.blob, cached.fileName);
        setStatus("idle");
        return;
      }

      const data = await downloadPropertyMasterData(refNum);
      if (!data?.url) {
        throw new Error("Download URL not found in response");
      }

      const fileResponse = await fetch(data.url);
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }

      const blob = await fileResponse.blob();
      const fileName = `${refNum}.zip`;
      await cacheReport(refNum, blob, fileName);
      clickDownloadLink(blob, fileName);
      setStatus("idle");
    } catch (err) {
      console.error("Error downloading property report:", err);
      setError(err.message || "Failed to download report.");
      setStatus("error");
    }
  }, []);

  return { status, error, download };
}
